import React from "react";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { IResumoTotalFeedbackResponse } from "types";
import { EstadoCarregando, EstadoErro, EstadoVazio } from "./Estados";

interface IGraficoResumo {
  /** Com um tipo selecionado, avisa que ele não se aplica aos pedidos. */
  tipoFiltrado: boolean;
  carregando: boolean;
  erro: string | null;
  resumo: IResumoTotalFeedbackResponse | null;
}

// Cores do tema do sistema (Purple): success e warning para os pedidos,
// azul e info (o mesmo par dos outros gráficos) para os feedbacks. A combinação
// passa no teste de daltonismo; amarelo e ciano são claros para o fundo, por
// isso cada segmento leva a contagem escrita, além da legenda e do tooltip.
const CORES = ["#1abc9c", "#f7b84b", "#4a81d4", "#4fc6e1"];
const COR_TEXTO = "#0b0b0b";
const COR_TEXTO_SECUNDARIO = "#52514e";
const COR_GRADE = "#e5e7eb";

const ALTURA_GRAFICO = 300;
// Altura da área do resumo, com ou sem gráfico: o Apex ocupa um pouco mais que
// ALTURA_GRAFICO (~15px), e os estados sem gráfico usam a mesma altura para o
// card não mudar de tamanho quando os filtros trocam.
const ALTURA_AREA = ALTURA_GRAFICO + 20;

// Abaixo disso o segmento é estreito demais para o rótulo caber.
const PERCENTUAL_MINIMO_ROTULO = 8;

const formatarNumero = (valor: number) => valor.toLocaleString("pt-BR");

/**
 * Pedidos e feedbacks têm escalas muito diferentes (dezenas x milhares), então
 * cada coluna mostra a proporção (100%) do seu próprio total e os números
 * absolutos vão nos rótulos, no eixo e no tooltip.
 */
const GraficoResumo: React.FC<IGraficoResumo> = ({
  tipoFiltrado,
  carregando,
  erro,
  resumo,
}) => {
  // Com um resumo anterior, a recarga mantém o gráfico (esmaecido) e o Apex
  // anima só a mudança de valores; o spinner fica para a primeira carga.
  const estado = carregando && !resumo ? (
    <EstadoCarregando texto="Carregando resumo..." />
  ) : erro ? (
    <EstadoErro mensagem={erro} />
  ) : !resumo || (!resumo.totalPedidos && !resumo.totalFeedbacks) ? (
    <EstadoVazio texto="Sem pedidos nem feedbacks no período." />
  ) : null;
  // Sem gráfico, o estado ocupa a mesma altura dele e fica centralizado, para
  // não desalinhar da coluna dos destaques.
  // O aviso do filtro de tipo sempre ocupa a sua linha (só fica invisível sem
  // filtro), senão a coluna cresce ao escolher um tipo e estica os destaques.
  const avisoTipo = (
    <small
      className="text-muted d-block"
      style={{ visibility: tipoFiltrado ? "visible" : "hidden" }}
      aria-hidden={!tipoFiltrado}
    >
      <i className="mdi mdi-information-outline me-1"></i>
      Pedidos não têm tipo: o filtro de tipo vale só para os feedbacks.
    </small>
  );

  if (estado || !resumo) {
    return (
      <>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ minHeight: ALTURA_AREA }}
        >
          <div className="w-100">{estado}</div>
        </div>
        {avisoTipo}
      </>
    );
  }

  const semAgradecimento = Math.max(resumo.totalFeedbacks - resumo.agradecidos, 0);
  const totais = [resumo.totalPedidos, resumo.totalFeedbacks];

  // Partes de cada coluna, de baixo para cima.
  const colunas = [
    [
      { nome: "Respondidos por todos", valor: resumo.respondidos, cor: CORES[0] },
      { nome: "Com resposta pendente", valor: resumo.naoRespondidos, cor: CORES[1] },
    ],
    [
      { nome: "Agradecidos", valor: resumo.agradecidos, cor: CORES[2] },
      { nome: "Sem agradecimento", valor: semAgradecimento, cor: CORES[3] },
    ],
  ];
  const categorias = [
    `Pedidos (${formatarNumero(resumo.totalPedidos)})`,
    `Feedbacks (${formatarNumero(resumo.totalFeedbacks)})`,
  ];

  // O Apex arredonda o topo por série (só a última), não por barra. Por isso
  // são duas séries, base e topo, com a cor definida por barra, e a parte mais
  // alta com valor vai sempre para o topo: se a coluna tem uma parte só (100%),
  // ela fica no topo e também sai arredondada. Coluna zerada fica sem barra,
  // senão o 100% divide por zero.
  const segmentos = colunas.map((partes) => {
    const comValor = partes.filter((parte) => parte.valor > 0);
    return comValor.length === 2 ? comValor : [null, comValor[0] ?? null];
  });
  const segmento = (serie: number, coluna: number) => segmentos[coluna][serie];

  const series = ["Base", "Topo"].map((name, serie) => ({
    name,
    data: categorias.map((x, coluna) => ({
      x,
      y: segmento(serie, coluna)?.valor ?? null,
      fillColor: segmento(serie, coluna)?.cor ?? CORES[0],
    })),
  }));

  const contagem = (serie: number, coluna: number) =>
    segmento(serie, coluna)?.valor ?? 0;
  // Legenda por coluna: as duas partes dos pedidos, depois as dos feedbacks.
  const itensLegenda = colunas.flat();

  const options: ApexOptions = {
    chart: {
      type: "bar",
      stacked: true,
      stackType: "100%",
      toolbar: { show: false },
      background: "transparent",
      fontFamily: "inherit",
      // Entrada rápida e sem escalonar série a série; nas trocas de filtro, as
      // barras só crescem/encolhem até os novos valores.
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 400,
        animateGradually: { enabled: false },
        dynamicAnimation: { enabled: true, speed: 350 },
      },
    },
    // Opacidade cheia: as cores validadas são as que aparecem na tela.
    fill: { opacity: 1 },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "70%",
        borderRadius: 4,
        borderRadiusApplication: "end",
        borderRadiusWhenStacked: "last",
      },
    },
    // Separação de 2px entre os segmentos, na cor do fundo.
    stroke: { show: true, width: 2, colors: ["#ffffff"] },
    dataLabels: {
      enabled: true,
      style: { fontSize: "12px", fontWeight: 600, colors: [COR_TEXTO] },
      dropShadow: { enabled: false },
      formatter: (percentual, { seriesIndex, dataPointIndex }) => {
        const valor = contagem(seriesIndex, dataPointIndex);
        const percentualValido = Number(percentual);
        if (!valor || !(percentualValido >= PERCENTUAL_MINIMO_ROTULO)) return "";
        return formatarNumero(valor);
      },
    },
    xaxis: {
      type: "category",
      labels: {
        style: { colors: [COR_TEXTO, COR_TEXTO], fontSize: "12px", fontWeight: 600 },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      max: 100,
      tickAmount: 4,
      labels: {
        formatter: (valor) => `${Math.round(Number(valor))}%`,
        style: { colors: [COR_TEXTO_SECUNDARIO], fontSize: "12px" },
        // O Apex desenha o texto ~2px acima da linha de grade; centraliza nela.
        offsetY: 2,
      },
    },
    grid: {
      borderColor: COR_GRADE,
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    // Legenda centralizada sob as colunas, no mesmo tamanho dos rótulos do eixo.
    // A legenda lista as 4 partes (as séries são só base e topo); clicar não
    // esconde nada, porque esconder uma série apagaria duas partes.
    legend: {
      show: true,
      showForSingleSeries: true,
      customLegendItems: itensLegenda.map(({ nome }) => nome),
      onItemClick: { toggleDataSeries: false },
      onItemHover: { highlightDataSeries: false },
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "13px",
      labels: { colors: COR_TEXTO_SECUNDARIO },
      markers: {
        width: 12,
        height: 12,
        radius: 3,
        offsetY: 1,
        fillColors: itensLegenda.map(({ cor }) => cor),
      },
      itemMargin: { horizontal: 10, vertical: 4 },
      offsetY: 4,
    },
    tooltip: {
      theme: "light",
      shared: false,
      intersect: true,
      y: {
        title: {
          formatter: (_serie, { seriesIndex, dataPointIndex }) =>
            `${segmento(seriesIndex, dataPointIndex)?.nome ?? ""}:`,
        },
        formatter: (_percentual, { seriesIndex, dataPointIndex }) => {
          const valor = contagem(seriesIndex, dataPointIndex);
          const total = totais[dataPointIndex];
          const parte = total ? Math.round((valor / total) * 100) : 0;
          return `${formatarNumero(valor)} de ${formatarNumero(total)} (${parte}%)`;
        },
      },
    },
    states: { active: { filter: { type: "none" } } },
  };

  return (
    <>
      <div
        role="img"
        aria-label={descricaoAcessivel(resumo, semAgradecimento)}
        aria-busy={carregando}
        style={{
          minHeight: ALTURA_AREA,
          opacity: carregando ? 0.5 : 1,
          transition: "opacity 0.2s",
        }}
      >
        <Chart options={options} series={series} type="bar" height={ALTURA_GRAFICO} />
      </div>
      {avisoTipo}
    </>
  );
};

// Descrição em texto dos mesmos números, para leitores de tela.
const descricaoAcessivel = (
  resumo: IResumoTotalFeedbackResponse,
  semAgradecimento: number,
) =>
  `Pedidos de feedback: ${resumo.totalPedidos}, sendo ${resumo.respondidos} respondidos ` +
  `por todos e ${resumo.naoRespondidos} com resposta pendente. Feedbacks: ` +
  `${resumo.totalFeedbacks}, sendo ${resumo.agradecidos} agradecidos e ` +
  `${semAgradecimento} sem agradecimento.`;

export default GraficoResumo;
